import React, { useContext, useMemo, useCallback, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { FakturaattestWrapperContext } from '../Contexts/FakturaattestWrapperContext';
import { EDFullscreenPaper } from '../../../../../../BaseComponents/FullscreenModal/Components/EDFullscreenPaper';
import ObjektVySida from '../../../../_Common/Components/BusinessCollectionComponents/ObjektVySida/ObjektVySida';
import ObjektVyKolumner, {
  ObjektVyKolumn,
} from '../../../../_Common/Components/BusinessCollectionComponents/ObjektVyKolumner/ObjektVyKolumner';
import { Reserver } from '../../../ObjektVyer/Attestant/Components/Reserver/Reserver';
import { ReservFor } from '../../../ObjektVyer/Attestant/Components/ReservFor/ReservFor';
import {
  AttestantQuery,
  AttestantQuery_node_TEDLRAttestant,
  AttestantQueryVariables,
  AttestantQuery_node_TEDLRAttestant__Associations_Reserver_All,
} from '../../../ObjektVyer/Attestant/Queries/_QueryTypes/AttestantQuery';
import attestantQuery from '../../../ObjektVyer/Attestant/Queries/AttestantQuery.gql';
import BusinessDataForm from '../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import { IClientBusinessModel } from '../../../../../../Types/ClientModelTypes';
import clientAvenyModel from '../../../../../../Model/clientAvenyModel';
import ObjektVy from '../../../../_Common/Components/BusinessCollectionComponents/ObjektVy/ObjektVy';
import {
  IFormData,
  removeIdsFromNewAssociations,
} from '../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import { useCollectionMutation } from '../../../../../../Components/QueryComponents/CollectionMutation/useCollectionMutation';
import UpdateMutation from '../../../ObjektVyer/Attestant/Mutations/AttestantUpdateMutation.gql';
import {
  AttestantUpdateMutation,
  AttestantUpdateMutationVariables,
} from '../../../ObjektVyer/Attestant/Mutations/_QueryTypes/AttestantUpdateMutation';
import { ActiveDatabaseContext } from '../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import AddReservButton from '../../../ObjektVyer/Attestant/Components/AddReservButton/AddReservButton';
import {
  ReservContext,
  IReservContext,
} from '../../../ObjektVyer/Attestant/Components/ReserverContext/ReserverContext';
import useNodeQuery from '../../../../../../Components/QueryComponents/NodeQuery/useNodeQuery';
import EDAvenyProgress from '../../../../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';
import Dialog from '../../../../../../BaseComponents/Dialog/EDDialog';
import { ObjektVyKort } from '../../../../_Common/Components/BusinessCollectionComponents/ObjektVyKort/ObjektVyKort';
import EDTypography from '../../../../../../BaseComponents/Typography/EDTypography';

interface IProps {
  attestantID: string;
}

const useStyles = makeStyles()(theme => ({
  container: {
    alignItems: 'flex-start',
    display: 'flex',
    flexFlow: 'column nowrap',
  },
  header: {
    backgroundColor: theme.palette.primary.dark,
  },
  contentWrapper: { margin: 0, height: '100%' },
}));

const attestantModel: IClientBusinessModel =
  clientAvenyModel.LR.BusinessModels.LRAttestant;

const ReservForDialog: React.FC<React.PropsWithChildren<IProps>> = props => {
  const fakturaattestContext = useContext(FakturaattestWrapperContext);
  const { activeDatabase } = useContext(ActiveDatabaseContext);
  const [openReserveDialog, setOpenReserveDialog] = useState(false);
  const [attestrattID, setAttestrattID] = useState<string | null>(null);

  const [editStandin, setEditStandin] =
    useState<AttestantQuery_node_TEDLRAttestant__Associations_Reserver_All>();

  const { classes } = useStyles();

  const {
    nodeData,
    loading: nodeLoading,
    refetch,
  } = useNodeQuery<
    AttestantQuery,
    AttestantQuery_node_TEDLRAttestant,
    AttestantQueryVariables
  >(attestantQuery, props.attestantID, {
    fetchPolicy: 'cache-and-network',
    skip: props.attestantID === '',
    variables: {
      id: props.attestantID,
      toDate: new Date().toLocaleDateString('sv-SE'),
    },
  });

  const [mutation, { loading, error }] = useCollectionMutation<
    AttestantUpdateMutation,
    AttestantUpdateMutationVariables
  >(UpdateMutation);

  const reservVyer = useMemo(() => {
    return [Reserver];
  }, []);

  const reservForVyer = useMemo(() => {
    return [ReservFor];
  }, []);

  const updateFunction = useCallback(
    (values: IFormData) => {
      const newValues = removeIdsFromNewAssociations(values ?? {});
      return mutation({
        variables: {
          db: activeDatabase || '',
          values: {
            id: nodeData?.id ?? '',
            _version: nodeData?._version ?? 1,
            ...newValues,
          },
          toDate: new Date().toLocaleDateString('sv-SE'),
        },
      });
    },
    [mutation, activeDatabase, nodeData?.id, nodeData?._version]
  );

  const contextValue: IReservContext = useMemo(
    () => ({
      dialogOpen: openReserveDialog,
      setDialogOpen: setOpenReserveDialog,
      attestrattID,
      setAttestrattID,
      editStandin,
      setEditStandin,
    }),
    [openReserveDialog, attestrattID, editStandin]
  );

  const isBFOAttestant = useMemo(
    () =>
      nodeData?._Associations?.Anvandare?.harKodbehorighetBokforder ||
      nodeData?._Associations?.Anvandare?.harKodbehorighetUtanordning,
    [
      nodeData?._Associations?.Anvandare?.harKodbehorighetBokforder,
      nodeData?._Associations?.Anvandare?.harKodbehorighetUtanordning,
    ]
  );

  return (
    <ReservContext.Provider value={contextValue}>
      <BusinessDataForm
        data={nodeData}
        dataLoading={nodeLoading}
        fullScreen={false}
        isNew={false}
        model={attestantModel}
        mutationError={error}
        mutationLoading={loading}
        updateMutation={updateFunction}
        refetchData={refetch}
      >
        <Dialog
          open={fakturaattestContext.openReservDialog}
          onClose={() => {
            fakturaattestContext.setOpenReservDialog(false);
          }}
          footerActions={[
            {
              label: 'Stäng',
              onClick: () => {
                fakturaattestContext.setOpenReservDialog(false);
              },
            },
          ]}
          title="Hantera frånvaro"
          fullHeight
          fullWidth
          maxWidth={false}
          classes={{ contentWrapper: classes.contentWrapper }}
        >
          {fakturaattestContext.reservForData &&
          !fakturaattestContext.loading &&
          nodeData &&
          !nodeLoading ? (
            <EDFullscreenPaper fullHeight>
              <ObjektVy
                nodeLoading={nodeLoading}
                sidaVidSida={false}
                hideEditMenu
              >
                <ObjektVySida
                  caption="Frånvaro"
                  sidNr={1}
                  collectionName="LRAttestrattReserv"
                  smallScreenNoPadding
                >
                  {isBFOAttestant && (
                    <ObjektVyKort noMargin>
                      <EDTypography>
                        Glöm ej att även registrera frånvaro i rutin
                        Bokföringsorder
                      </EDTypography>
                    </ObjektVyKort>
                  )}
                  <ObjektVyKolumner>
                    <ObjektVyKolumn fullPage>
                      <ObjektVyKort
                        readOnly
                        bmod={attestantModel}
                        button={() => (
                          <AddReservButton
                            setEditStandin={setEditStandin}
                            setDialogOpen={setOpenReserveDialog}
                          />
                        )}
                        caption="Frånvaro"
                        id={props.attestantID ?? null}
                        vyer={reservVyer}
                      />
                    </ObjektVyKolumn>
                    <ObjektVyKolumn fullPage>
                      <ObjektVyKort
                        readOnly
                        bmod={attestantModel}
                        caption="Reserv för"
                        id={props.attestantID}
                        vyer={reservForVyer}
                      />
                    </ObjektVyKolumn>
                  </ObjektVyKolumner>
                </ObjektVySida>
              </ObjektVy>
            </EDFullscreenPaper>
          ) : (
            <EDAvenyProgress />
          )}
        </Dialog>
      </BusinessDataForm>
    </ReservContext.Provider>
  );
};

export default ReservForDialog;
